import Action from "./class.Action";
import * as Input from "../class.TransporterInput";
import * as ActionUtils from "./class.ActionUtils";

/**
 * Creates a bubble with single or multi-select buttons as a response to a problem.
 */
export default class MultiAnswerProblem extends Action {
	constructor(options) {
		super(options);

		// A reference to the last bubble rendered.
		this.activeBubbleView = null;
		// Is this problem Multiple Select?
		this.isMultiSelect = this.model.type === "multiple-multi-select";
		// Delimiter for responses
		this.delimiter = this.model.new_lines ? ",<br/>" : ", ";

		// parallel array (to this.model.options) of Input objects.
		this.inputs = [];
		// parallel array of which options are selected.
		this.selected = Array(this.model.options.length).fill(false);
		// parallel array of which options are disabled.
		this.disabled = Array(this.model.options.length).fill(false);

		// The attempt we are on.
		this.attempt = 0;
		// Number of attempts before permitting the Give Up button.
		this.attemptsRemaining = 1;
		// Whether the Give Up button was clicked
		this.gaveUp = false;
		this.lastHint = null;

		// Track submissions the user already tried.
		this.submissions = [];

		this.initializeAnalytics();
		this.renderProblem(this.$anchor);
	}

	/**
	 * Traverse the flow after this problem has been passed.
	 * @param {*} $problemAnchor The element to render after.
	 */
	passProblem($problemAnchor) {
		// Throw out our reference to our last bubble. We don't need it anymore.
		if (this.activeBubbleView) {
			this.activeBubbleView = null;
		}

		// Default trigger is "trigger".
		let trigger = "trigger";

		const hasGiveUpTrigger =
			this.model["give-up-trigger"] && this.model["give-up-trigger"].length;

		// Use the give up trigger if necessary.
		if (this.gaveUp) {
			if (this.taTrial) {
				this.taTrial.setGaveUp();
			}
			if (hasGiveUpTrigger) {
				trigger = "give-up-trigger";
			}
		} else {
			if (this.taTrial) {
				this.taTrial.setPassed();
			}
		}

		// Traverse the trigger, and then advance the script.
		this.runnerController.createAndTraverseFlow({
			pointer: this.pointer,
			item: this.model,
			flow: [{traverse: trigger}],
			onAdvance: ($anchor) => {
				this.onAdvance($anchor);
			},
			onBypass: () => {
				this.onBypass();
			},
			$anchor: $problemAnchor,
			context: this.context,
		});
	}

	/**
	 * Renders a bubble with multiple buttons as responses to the problem.
	 * @param {*} $problemAnchor The element to render after.
	 */
	async renderProblem($problemAnchor) {
		if (this.taItem) {
			this.taTrial = this.taItem.createTrial({
				attempt: this.attempt,
				last_hint: this.lastHint,
			});
		}

		// If no anchor was provided, fallback to the one provided by TransporterRunner.
		if (!$problemAnchor) {
			$problemAnchor = this.$anchor;
		}

		// Reset selections
		this.selected.fill(false);

		/**
		 * Is the anchor our LayoutView? This is important, since we always
		 * want to "append" our bubbles to the layout, rather than place
		 * them "after" it.
		 */
		const anchorIsLayout = $problemAnchor.is(this.layoutView.$el);

		// Create the bubble.
		let probBubbleView;

		if (this.model.center) {
			// Create a center bubble and append virtual bubbles to it
			// for anything in the prompt.
			if (anchorIsLayout) {
				probBubbleView = this.layoutView.placeBubble(
					$problemAnchor,
					"append",
					"whiteboard"
				);
			} else {
				probBubbleView = this.layoutView.placeBubble(
					$problemAnchor,
					"after",
					"whiteboard"
				);
			}
			for (let prim of this.model.body) {
				if (prim.html || prim.text) {
					const virtualBubble = probBubbleView.appendVirtualBubble();
					virtualBubble
						.appendContent($(`<div/>`).html(prim.html || prim.text))
						.setTextAlign(this.model.text_align || "center");
				}
			}
		} else {
			// We must render our prompt, and then set up our problem bubble.
			let lastPrim;
			for (let prim of this.model.body) {
				if (prim.html || prim.text) {
					let anchor;
					let method;
					if (anchorIsLayout) {
						method = "append";
						anchor = this.layoutView.$el;
					} else {
						method = "after";
						if (lastPrim) {
							anchor = lastPrim.$el;
						} else {
							anchor = $problemAnchor;
						}
					}
					lastPrim = this.layoutView.placeBubble(anchor, method, "teacher");
					lastPrim.appendContent($("<div />").html(prim.html || prim.text));
					if (this.model.text_align) {
						lastPrim.setTextAlign(this.model.text_align);
					}
					/* eslint-disable-next-line no-await-in-loop */
					await this.animationController.FadeIn.start({
						bubbleView: lastPrim,
					}).scroll();
				}
			}
			probBubbleView = this.layoutView.placeBubble(
				lastPrim ? lastPrim.$el : $problemAnchor,
				"after",
				"whiteboard"
			);
		}

		// Finally, let's render the multiple choice stuff now...
		const $buttonDiv = $(`<div class="btn-answer-problem"/>`);
		const $submitDiv = $(`<div class="btn-submit-container"/>`);
		this.activeBubbleView = probBubbleView;

		// Start generating buttons for every option.
		if (_.isArray(this.model.options) && this.model.options.length) {
			// Create the option buttons.
			_.each(this.model.options, (option, key) => {
				if (option) {
					// Create button and append to bubble.
					const button = new Input.Button({
						attributes: {
							inputController: this.inputController,
							className: "btn-like",
							tagName: "a",
							target: this.pointerStr + key + "Attempt" + this.attempt,
						},
					});
					this.inputs.push(button);
					button.$el.addClass("color" + (key % 8)).addClass("light");
					$buttonDiv.append(button.$el);

					// Set the button text.
					if (option.content) {
						button.$el.html(
							$("<div class='inner-content' />").html(option.content.html)
						);
					} else {
						button.$el.html(
							$("<div class='inner-content' />").html(option.match)
						);
					}

					if (this.disabled[key]) {
						button.$el.removeAttr("id");
						button.$el.addClass("off");
					} else {
						// Bind the button click.
						button.onClick({
							callback: async () => {
								if (this.taItem) {
									this.taItem.setInteracted();
								}
								if (this.activeBubbleView) {
									probBubbleView.$el.css({
										marginBottom: "",
									});

									// Update the selected attribute.
									if (!this.isMultiSelect) {
										_.each(_.without(this.inputs, button), (input) => {
											input.$el.removeClass("selected");
											this.selected.fill(false);
										});
										button.$el.addClass("selected");
									} else {
										button.$el.toggleClass("selected");
									}
									this.selected[key] = button.$el.hasClass("selected");

									// Calculate response
									let response = _.reduce(
										this.model.options,
										(memo, opt, i) => {
											if (this.selected[i]) {
												return (
													memo +
													(memo.length > 0 ? this.delimiter : "") +
													(opt.content.html || opt.content.text)
												);
											}
											return memo;
										},
										""
									);
									let doAnimation = false;
									if (!this.activeBubbleView.$el.hasClass("student")) {
										// Make the student bubble
										let targetAnchor;
										if (this.activeBubbleView.virtual) {
											targetAnchor = $problemAnchor;
										} else {
											targetAnchor = this.activeBubbleView.$el;
										}
										this.activeBubbleView = this.layoutView.placeBubble(
											targetAnchor,
											"after",
											"student"
										);
										this.activeBubbleView.setContent(
											$('<div class="response"></div>')
										);
										this.activeBubbleView.appendContent($submitDiv);
										this.activeBubbleView.$el.addClass("short-answer");
										doAnimation = true;
									}
									this.activeBubbleView.$el
										.find(".response")
										.html(
											response.length > 0 && !this.model.hideResponse
												? response
												: ""
										);
									if (response.length) {
										submitButton.$el.removeClass("off");
									} else {
										submitButton.$el.addClass("off");
									}
									if (doAnimation) {
										await this.animationController.FadeIn.start({
											bubbleView: this.activeBubbleView,
										}).scroll();
									}
									this.inputController.request({enableAnimations: true});
								}
							},
						});
					}
				}
			});

			probBubbleView.appendContent($buttonDiv);

			// Cheese the flex-wrap to make 4 elements split into 2-2
			// and 5 elements split into 2-3.
			if (this.model.options.length === 5) {
				$buttonDiv
					.children()
					.eq(2)
					.after($(`<div style="flex-basis: 100%;"></div>`));
			}

			if (this.isMultiSelect && this.attemptsRemaining <= 0) {
				// Create the Give Up button.
				$submitDiv.addClass("has-hint");
				const surrenderButton = new Input.Button({
					attributes: {
						inputController: this.inputController,
						className: "btn-like",
						tagName: "a",
						target: this.pointerStr + "Attempt" + this.attempt + "Surrender",
					},
				});
				this.inputs.push(surrenderButton);
				surrenderButton.$el.html("Give Up");
				surrenderButton.$el.addClass("color3");

				// Bind the give up button's action.
				surrenderButton.onClick({
					once: true,
					callback: async () => {
						if (this.taItem) {
							this.taItem.setInteracted();
						}
						if (this.taTrial) {
							this.taTrial.setGaveUp();
						}
						// Deactivate buttons
						while (this.inputs.length > 0) {
							this.inputs.pop().off();
						}
						this.activeBubbleView.$el.removeClass("short-answer");
						this.activeBubbleView.setContent("I give up.");
						await this.animationController.FadeIn.start({
							bubbleView: this.activeBubbleView,
						}).scroll();

						this.gaveUp = true;
						this.passProblem(this.activeBubbleView.$el);
					},
				});
				$submitDiv.append(surrenderButton.$el);
			}

			// Create the submit button
			const submitButton = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target: this.pointerStr + "Attempt" + this.attempt + "Submit",
				},
			});
			this.inputs.push(submitButton);
			submitButton.$el.html("Submit");
			submitButton.$el.addClass("color2");
			$submitDiv.append(submitButton.$el);

			// Bind the submit button
			submitButton.onClick({
				once: true,
				callback: async () => {
					if (this.taItem) {
						this.taItem.setInteracted();
					}
					// Update remaining attempts.
					this.attempt++;
					this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);

					// Deactivate buttons
					while (this.inputs.length > 0) {
						this.inputs.pop().off();
					}

					// Place the student response bubble.
					const response = _.reduce(
						this.model.options,
						(memo, option, key) => {
							if (this.selected[key]) {
								return (
									memo +
									(memo.length > 0 ? this.delimiter : "") +
									(option.content.html || option.content.text)
								);
							}
							return memo;
						},
						""
					);
					this.activeBubbleView.$el.removeClass("short-answer");

					let content = response;
					if (this.model.hideResponse) {
						content = "I'm done.";
					}
					if (response.length === 0) {
						content = $("<em>None of the above.</em>");
					}
					this.activeBubbleView.setContent(content);

					if (this.taTrial) {
						this.taTrial.setResponse(
							this.selected.indexOf(true) >= 0
								? JSON.stringify(
										this.selected
											.map((_, index) => index)
											.filter((index) => this.selected[index])
								  )
								: null
						);
					}
					await this.animationController.FadeIn.start({
						bubbleView: this.activeBubbleView,
					}).scroll();
					const responseBubble = this.activeBubbleView;

					// Process the number of correct answers
					if (!this.isMultiSelect) {
						// Single select procedure
						let singleOption = this.model.options[this.selected.indexOf(true)];
						const alreadySubmitted = this.submissions.indexOf(response) >= 0;
						this.submissions.push(response);
						let key = this.selected.indexOf(true);
						this.disabled[key] = true;

						if (alreadySubmitted && !singleOption.is_correct) {
							if (this.taTrial) {
								this.taTrial.setFailed();
							}
							responseBubble.fail();
							this.renderTeacherResponse(this.activeBubbleView.$el, true);
						} else if (singleOption.trigger) {
							if (singleOption.is_correct) {
								responseBubble.pass();
							} else {
								if (this.taTrial) {
									this.taTrial.setFailed();
								}
								responseBubble.fail();
							}
							// Traverse the flow for this option.
							this.runnerController.createAndTraverseFlow({
								pointer: this.pointer.concat(["options", key]),
								context: this.context,
								item: singleOption,
								flow: [{traverse: "trigger"}],
								onAdvance: ($anchor) => {
									if (singleOption.is_correct) {
										this.passProblem($anchor);
									} else {
										this.lastHint = key;
										this.renderProblem($anchor);
									}
								},
								onBypass: () => {
									this.onBypass();
								},
								$anchor: this.activeBubbleView.$el,
							});
						} else if (singleOption.is_correct) {
							// Correct with no trigger
							responseBubble.pass();
							this.passProblem(this.activeBubbleView.$el);
						} else {
							// Wrong with no trigger.
							if (this.taTrial) {
								this.taTrial.setFailed();
							}
							responseBubble.fail();
							await this.renderTeacherResponse(this.activeBubbleView.$el);
						}
					} else {
						// Multi select procedure
						const numberCorrect = _.reduce(
							this.model.options,
							(memo, option, i) => {
								return (
									memo + (!!(option.is_correct || 0) === !!this.selected[i])
								);
							},
							0
						);

						if (
							numberCorrect >=
							(this.model.num_correct || this.model.options.length)
						) {
							responseBubble.pass();
							this.passProblem(this.activeBubbleView.$el);
						} else {
							if (this.taTrial) {
								this.taTrial.setFailed();
							}
							responseBubble.fail();
							const alreadySubmitted = this.submissions.indexOf(response) >= 0;
							this.submissions.push(response);
							await this.renderTeacherResponse(
								this.activeBubbleView.$el,
								alreadySubmitted && this.attempt > 2
							);
						}
					}
				},
			});

			// Bind the enter key to submit.
			$buttonDiv
				.attr("tabindex", 0)
				.focus(() => {
					$buttonDiv.css("outline", "none");
				})
				.keypress((event) => {
					if (event.keyCode === 13) {
						if (submitButton.$el.is(":visible")) {
							submitButton.$el.trigger("click");
						}
					} else if (event.keyCode >= 48 && event.keyCode < 58) {
						// Bind number keys to the options.
						const num = event.keyCode === 48 ? 9 : event.keyCode - 49;
						const $buttons = $buttonDiv.children();
						if (num < $buttons.length) {
							$buttons.eq(num).trigger("click");
						}
					}
				});
		} else {
			// Create a button
			const button = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target: this.pointerStr,
				},
			});
			$buttonDiv.append(button.$el);

			// Set the button text
			const passText = "¯\\_(ツ)_/¯";
			button.$el.text(passText);

			// Bind the button
			button.onClick({
				once: true,
				callback: () => {
					if (this.taItem) {
						this.taItem.setInteracted();
					}
					// Update the bubble.
					const $new_status = $("<div />").text(passText);
					probBubbleView.setContent($new_status);
					this.passProblem(probBubbleView.$el);
				},
			});
			this.activeBubbleView = probBubbleView;
		}

		if (this.mainView.$el.height() > 390) {
			probBubbleView.$el.css({
				marginBottom: "130px",
			});
		}
		await this.animationController.FadeIn.start({
			bubbleView: probBubbleView,
		}).scroll();
		probBubbleView.$el.css({
			marginBottom: "",
		});
		$buttonDiv.focus();
		this.inputController.request({enableAnimations: true});
	}

	/**
	 * Render's a default response from the teacher's side.
	 * Mutates `this.activeBubbleView`.
	 *
	 * @param {*} $anchor
	 */
	async renderTeacherResponse($anchor, alreadySubmitted = false) {
		if (alreadySubmitted) {
			// Response for if the user already submitted a thing.
			const teacherBubbleView = this.layoutView.placeBubble(
				$anchor,
				"after",
				"teacher"
			);
			this.activeBubbleView = teacherBubbleView;
			teacherBubbleView.setContent(
				ActionUtils.getRandomTeacherResponse("repeat")
			);
			await this.animationController.FadeIn.start({
				bubbleView: teacherBubbleView,
			}).scroll();
			this.renderProblem(this.activeBubbleView.$el);
		} else {
			let hintIndex = NaN;
			if (this.isMultiSelect) {
				// First attempt check removed.
				// Still checks to prevent old replays (playthrough_id < 18125) from breaking.
				if (this.playthrough_id < 18125 && this.attempt === 1) {
					// For the first attempt, let's send a generic message.
					let hasIncorrectAnswer = false;
					for (let i = 0; i < this.model.options.length; i++) {
						const option = this.model.options[i];
						const isSelected = this.selected[i];
						if (!option.is_correct && isSelected) {
							hasIncorrectAnswer = true;
							break;
						}
					}
					const teacherBubbleView = this.layoutView.placeBubble(
						$anchor,
						"after",
						"teacher"
					);
					this.activeBubbleView = teacherBubbleView;
					if (hasIncorrectAnswer) {
						// The user marked selected a wrong answer.
						teacherBubbleView.setContent(
							"It looks like you included a wrong answer."
						);
					} else {
						// The user is missing a correct answer.
						teacherBubbleView.setContent("Are you sure that's all of them?");
					}
					await this.animationController.FadeIn.start({
						bubbleView: teacherBubbleView,
					}).scroll();
					return this.renderProblem(this.activeBubbleView.$el);
				} else {
					// For the next attempts, let's be a little more specific.
					// First, look for any incorrect answers marked true.
					let altHintIndex = NaN;
					for (let i = 0; i < this.model.options.length; i++) {
						const option = this.model.options[i];
						const isSelected = this.selected[i];
						const hasTrigger = this.model.options[i].trigger;
						if (
							_.isNaN(hintIndex) &&
							!option.is_correct &&
							isSelected &&
							hasTrigger
						) {
							// Found it... save this index so we can render it.
							hintIndex = i;
						} else if (
							_.isNaN(altHintIndex) &&
							option.is_correct &&
							!isSelected &&
							hasTrigger
						) {
							// While we're in this loop, let's also save the first index
							// of any correct answer marked false.
							altHintIndex = i;
						}
					}
					if (_.isNaN(hintIndex)) {
						hintIndex = altHintIndex;
					}
				}
			}

			if (isNaN(hintIndex)) {
				// No hint, render a generic response.
				const teacherBubbleView = this.layoutView.placeBubble(
					$anchor,
					"after",
					"teacher"
				);
				this.activeBubbleView = teacherBubbleView;

				teacherBubbleView.setContent(
					ActionUtils.getRandomTeacherResponse("negative")
				);
				await this.animationController.FadeIn.start({
					bubbleView: teacherBubbleView,
				}).scroll();
				this.renderProblem(this.activeBubbleView.$el);
			} else {
				// There's a hint! Show it, yo!
				this.lastHint = hintIndex;
				this.runnerController.createAndTraverseFlow({
					pointer: this.pointer.concat(["options", hintIndex]),
					item: this.model.options[hintIndex],
					flow: [{traverse: "trigger"}],
					onAdvance: ($newAnchor) => {
						this.renderProblem($newAnchor);
					},
					onBypass: () => {
						this.onBypass();
					},
					$anchor: this.activeBubbleView.$el,
					context: this.context,
				});
			}
		}
	}
}
